/**
 * Una fila del editor de medios de pago: la caja (que impone la moneda), el
 * medio si la caja acepta mas de uno, el importe y la X para quitarla.
 *
 * No decide plata: recibe el pago y devuelve los cambios. Corregir el medio
 * cuando cambia la caja lo hace `EditorPagos`, que es quien ve la lista.
 */
import { X } from "lucide-react";

import { InputImporte } from "@/components/money/input-importe";
import { IconButton } from "@/components/ui/icon-button";
import { Segmented } from "@/components/ui/segmented";
import { SelectNativo } from "@/components/ui/select-nativo";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  esCheque,
  mediosDe,
  nombreCaja,
  type CuentaFinanciera,
  type DatosCheque,
  type PagoCobro,
} from "@/components/cobro/logica";

export interface FilaPagoProps {
  pago: PagoCobro;
  cuentas: CuentaFinanciera[];
  onChange: (cambios: Partial<PagoCobro>) => void;
  /** Sin esto no se dibuja la X: la fila no se puede quitar. */
  onQuitar?: () => void;
}

export function FilaPago({ pago, cuentas, onChange, onQuitar }: FilaPagoProps): JSX.Element {
  const cuenta = cuentas.find((c) => c.id === pago.cuenta_id);
  const medios = cuenta ? mediosDe(cuenta) : [];
  const preguntaMedio = medios.length > 1;
  const activas = cuentas.filter((c) => c.activo !== false);

  const pideCheque = esCheque(pago.medio);
  const ch = pago.cheque;

  /** Cambia un campo del cheque sin perder los otros. */
  const cambiarCheque = (campo: keyof DatosCheque, valor: string): void =>
    onChange({
      cheque: {
        numero: ch?.numero ?? "",
        banco: ch?.banco ?? "",
        librador: ch?.librador ?? "",
        fecha_cobro: ch?.fecha_cobro ?? "",
        [campo]: valor,
      },
    });

  return (
    <div className="flex flex-col gap-2">
      {/* `items-start` y no `items-center`: cuando el importe muestra su error o
        el eco, la linea extra queda debajo del campo y no descoloca la fila. */}
      {/* En el celular la caja ocupa su propia linea y el importe baja: con los
        dos en la misma, el select cortaba en «Efectivo (» y no se sabia si era
        la caja de pesos o la de dolares. */}
      {/* En el celular la caja se elige con botones grandes, uno por caja: el
          desplegable cortaba el nombre y había que abrirlo para saber si era
          la caja de pesos o la de dólares. En la compu sigue el desplegable. */}
      <Segmented
        etiqueta="Elegí la caja"
        apariencia="chips"
        tamano="md"
        className="grid grid-cols-2 sm:hidden [&>*]:justify-center [&>*]:whitespace-normal [&>*]:text-center"
        opciones={activas.map((c) => ({ valor: String(c.id), etiqueta: nombreCaja(c) }))}
        valor={String(pago.cuenta_id)}
        onChange={(v) => onChange({ cuenta_id: Number(v) })}
      />
      <div className="flex flex-wrap items-start gap-2 sm:flex-nowrap">
        <SelectNativo
          value={pago.cuenta_id}
          onChange={(e) => onChange({ cuenta_id: Number(e.target.value) })}
          className="min-w-0 flex-1 basis-full max-sm:hidden sm:basis-0"
          aria-label="Caja"
        >
          {activas.map((c) => (
            <option key={c.id} value={c.id}>
              {nombreCaja(c)}
            </option>
          ))}
        </SelectNativo>

        {/* Un select con UNA sola opcion no es una eleccion: es una palabra al
          lado de la misma palabra. En una caja de efectivo la cuenta ya fija el
          medio (pesos -> Efectivo, dolares -> Dolar billete) y el editor lo
          corrige solo al cambiar de cuenta, asi que el valor siempre es valido
          este el control dibujado o no. Solo se dibuja donde hay algo que
          decidir: el banco. */}
        {preguntaMedio ? (
          <SelectNativo
            value={pago.medio}
            onChange={(e) => onChange({ medio: e.target.value })}
            className="w-28 shrink-0"
            aria-label="Medio de pago"
          >
            {medios.map((m) => (
              <option key={m.valor} value={m.valor}>
                {m.etiqueta}
              </option>
            ))}
          </SelectNativo>
        ) : null}

        {/* Sin `onMonedaChange`, InputImporte deja la moneda FIJA (es imposible
          tipear pesos en una fila de dolares) y no dibuja el selector, con lo
          cual el ancho es todo del campo. `w-36` y no `w-32`: tiene que entrar
          un importe de siete cifras, que es lo que cuesta un iPhone en pesos. */}
        <InputImporte
          valor={pago.importe}
          moneda={cuenta?.moneda ?? "ARS"}
          requerido
          onValorChange={(v) => onChange({ importe: v })}
          // Sin la ✕ de vaciar: al lado va la ✕ de quitar el medio, y dos ✕
          // iguales a 12 px una de otra se tocaban por error.
          vaciable={false}
          className="min-w-0 flex-1 sm:w-36 sm:flex-none sm:shrink-0"
        />

        {onQuitar ? (
          <IconButton
            icono={X}
            aria-label="Quitar medio de pago"
            onClick={onQuitar}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          />
        ) : null}
      </div>

      {/* Los datos del papel. Sin esto el cobro con cheque era un camino sin
        salida: la caja de tipo BANCO ofrecia el medio y el servidor lo
        rechazaba porque `esquemaPago` exige el objeto `cheque`. Los cuatro
        campos son obligatorios alla, asi que se piden los cuatro aca. */}
      {pideCheque ? (
        <div className="flex flex-wrap items-start gap-2 pl-2">
          {/* Cada campo lleva su rotulo a la vista. Con solo el placeholder,
            una vez completos quedaban tres cajas de texto iguales y una fecha
            sin decir que era la de cobro. */}
          <DatoCheque rotulo="Número" className="w-32">
            <Input
              value={ch?.numero ?? ""}
              onChange={(e) => cambiarCheque("numero", e.target.value)}
              placeholder="Número"
              aria-label="Número del cheque"
            />
          </DatoCheque>
          <DatoCheque rotulo="Banco" className="w-36">
            <Input
              value={ch?.banco ?? ""}
              onChange={(e) => cambiarCheque("banco", e.target.value)}
              placeholder="Banco"
              aria-label="Banco del cheque"
            />
          </DatoCheque>
          <DatoCheque rotulo="Librador" className="w-40">
            <Input
              value={ch?.librador ?? ""}
              onChange={(e) => cambiarCheque("librador", e.target.value)}
              placeholder="Librador"
              aria-label="Librador del cheque"
            />
          </DatoCheque>
          <DatoCheque rotulo="Se cobra el" className="w-40">
            <Input
              type="date"
              value={ch?.fecha_cobro ?? ""}
              onChange={(e) => cambiarCheque("fecha_cobro", e.target.value)}
              aria-label="Fecha de cobro del cheque"
            />
          </DatoCheque>
        </div>
      ) : null}
    </div>
  );
}

/** Un dato del cheque con su rotulo chico arriba. El `<label>` envuelve al campo. */
function DatoCheque({
  rotulo,
  className,
  children,
}: {
  rotulo: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-xs text-muted-foreground">{rotulo}</span>
      {children}
    </label>
  );
}
